import {
  BooleanField,
  ChipField,
  DateField,
  EditButton,
  FunctionField,
  ReferenceArrayField,
  ReferenceField,
  SelectField,
  Show,
  SingleFieldList,
  TextField,
  Title,
  TopToolbar,
  useRecordContext,
} from "react-admin";
import { TabbedShowLayout } from "../common/TabbedShowLayout";
import {
  DISCOUNT_COUPON_APPLIES_TO_CHOICES,
  DISCOUNT_COUPON_REDEMPTION_CHOICES,
  DISCOUNT_COUPON_TYPE_CHOICES,
  DISCOUNT_COUPON_STOCK_TYPE,
  isRestaurantFund,
  isMorfyFund,
  DISCOUNT_COUPON_DISCOUNT_TYPE_CHOICES,
  DISCOUNT_COUPON_FUNDING_CHOICES,
} from "../utils/DiscountCouponUtil";
import { STOCK_TYPES } from "../enums/StockTypes";
import { DISCOUNT_COUPON_TYPE } from "../enums/DiscountCouponType";
import DynamicReferenceField from "./DynamicReferenceField";
import DiscountValueField from "./DiscountValueField";
import { PriceField } from "../common/PriceField";
import { COUNTRIES_SHORT } from "../enums/Countries";
import {
  formatNumberToPercentage,
  formatNumberToPrice,
} from "../utils/NumberUtils";

const DiscountCouponTitle = () => {
  const record = useRecordContext();
  return <Title title={`Cupón "${record?.title}"`} />;
};

const DiscountCouponShowActions = () => {
  return (
    <TopToolbar>
      <EditButton />
    </TopToolbar>
  );
};

const DiscountCouponShow = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <>
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label="Datos básicos">
          <TextField source="id" label="ID" />
          <TextField source="title" label="Título" />
          <TextField source="description" label="Descripción" />
          <TextField source="code" label="Código" />
          <DateField
            source="expirationDate"
            label="Fecha de expiración"
            showTime={true}
          />
          <SelectField
            source="redemptionCondition"
            label="Condiciones de canje"
            choices={DISCOUNT_COUPON_REDEMPTION_CHOICES}
          />
          <SelectField
            source="type"
            label="Audiencia"
            choices={DISCOUNT_COUPON_TYPE_CHOICES}
          />
          {record?.type === DISCOUNT_COUPON_TYPE.TARGETED && (
            <ReferenceField label="Usuario" source="user" reference="users">
              <FunctionField
                render={(record) => {
                  if (!record) return "-";
                  return `${record.profile.firstName} ${record.profile.lastName}`;
                }}
              />
            </ReferenceField>
          )}
          <BooleanField source="active" label="Activo" />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Descuento">
          <SelectField
            source="funding.fundedBy"
            label="Pagado por"
            choices={DISCOUNT_COUPON_FUNDING_CHOICES}
          />
          {isRestaurantFund(record.funding.fundedBy) && (
            <FunctionField
              source="funding.restaurantFund"
              label="Descuento Restaurante"
              render={(record) => {
                if (!record.discount) return "-";
                if (record.discount.type === "PERCENTAGE") {
                  return formatNumberToPercentage(
                    record.funding.restaurantFund
                  );
                } else if (record.discount.type === "AMOUNT") {
                  return formatNumberToPrice(record.funding.restaurantFund);
                }
                return "-";
              }}
            />
          )}
          {isMorfyFund(record.funding.fundedBy) && (
            <FunctionField
              source="funding.morfyFund"
              label="Descuento Morfy"
              render={(record) => {
                if (!record.discount) return "-";
                if (record.discount.type === "PERCENTAGE") {
                  return formatNumberToPercentage(record.funding.morfyFund);
                } else if (record.discount.type === "AMOUNT") {
                  return formatNumberToPrice(record.funding.morfyFund);
                }
                return "-";
              }}
            />
          )}
          <SelectField
            source="discount.type"
            label="Tipo de descuento"
            choices={DISCOUNT_COUPON_DISCOUNT_TYPE_CHOICES}
          />
          <DiscountValueField source="discount" label="Valor del descuento" />
          <PriceField
            source="discount.maxDiscountLimit"
            label="Tope de descuento"
            country={COUNTRIES_SHORT.CL} // TODO: uncomment when Argentina is supported
            emptyText="Sin límite"
          />
          <PriceField
            source="discount.minPurchaseAmount"
            label="Mínimo de compra"
            country={COUNTRIES_SHORT.CL} // TODO: uncomment when Argentina is supported
            emptyText="Sin mínimo"
          />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Aplica a">
          <SelectField
            source="appliesTo.type"
            label="Aplica a"
            choices={DISCOUNT_COUPON_APPLIES_TO_CHOICES}
          />
          <FunctionField
            label="Entidades"
            render={(record) => <DynamicReferenceField record={record} />}
          />
          <ReferenceArrayField
            source="appliesTo.regions"
            reference="regions"
            perPage={100}
            label="Regiones"
          >
            <SingleFieldList linkType={false}>
              <ChipField source="name" size="small" />
            </SingleFieldList>
          </ReferenceArrayField>
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Usos">
          <SelectField
            source="stockType"
            label="Tipo de Uso"
            choices={DISCOUNT_COUPON_STOCK_TYPE}
          />
          <FunctionField
            source="stock"
            label="Máximo de usos"
            render={(record) => {
              if (record?.stockType === STOCK_TYPES.UNLIMITED) {
                return "Ilimitado";
              }
              return record?.stock;
            }}
          />
        </TabbedShowLayout.Tab>
      </TabbedShowLayout>
    </>
  );
};

const DiscountCouponContainer = () => (
  <Show title={<DiscountCouponTitle />} actions={<DiscountCouponShowActions />}>
    <DiscountCouponShow />
  </Show>
);

export default DiscountCouponContainer;
